import React from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, X } from 'lucide-react';
import { AlternateRecipe } from '@/types/alternate-recipe';
import { DEFAULT_RECIPE_IMAGE } from '@/constants/media';
import { formatMacro } from '@/utils/gram-ml';
import { getDietaryDisplayName } from '@/data/dietary-mapping';
import { Tag } from '@/components/ui/tag';
export type SmartEditMealContext = {
  day: number;
  mealName: string;
  mealOrder: number;
  currentRecipeId: string;
  recipeId: string;
  recipeName: string;
  recipeImage: string;
  source: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  alternateRecipe?: AlternateRecipe;
};

export type SmartEditSuggestion = {
  id: string;
  title: string;
  image: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  tags: string[];
  bestFor: string;
  alternateRecipe?: AlternateRecipe;
  dietLabels?: string[];
  cuisines?: string[];
  cuisineTypes?: string[];
  generatedVia?: 'shuffle' | 'smart_edit' | string | null;
  generationIndicator?: AlternateRecipe['generationIndicator'];
  generatedFromTitle?: string | null;
};

type SmartEditModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meal: SmartEditMealContext | null;
  prompt: string;
  onPromptChange: (value: string) => void;
  onPresetSelect: (value: string) => void;
  onSendPrompt: () => void;
  suggestions: SmartEditSuggestion[];
  selectedSuggestionId?: string | null;
  onSelectSuggestion?: (suggestionId: string) => void;
  inProgressNotice?: string | null;
  generating?: boolean;
  applying?: boolean;
  attemptsLeft?: number;
  attemptsUsed?: number;
  dailyLimit?: number;
  generateDisabled?: boolean;
};

const PRESET_PROMPTS = [
  'Make it vegetarian',
  'Higher in fiber',
  'Reduce calories',
  'More protein',
  'Something lighter',
  'Different protein',
];

const getGeneratedIndicatorLabel = (suggestion: SmartEditSuggestion): string | null => {
  const explicitLabel = typeof suggestion.generationIndicator?.label === 'string'
    ? suggestion.generationIndicator.label.trim()
    : '';
  if (explicitLabel) return explicitLabel;

  const generatedVia = typeof suggestion.generatedVia === 'string'
    ? suggestion.generatedVia.trim().toLowerCase()
    : '';
  if (generatedVia === 'smart_edit') return 'Smart Edit';
  if (generatedVia === 'shuffle' || generatedVia === 'regenerate') return 'Regenerated';
  return null;
};

const visibleTags = (values: unknown): string[] => {
  if (!Array.isArray(values)) return [];
  const seen = new Set<string>();
  const tags: string[] = [];

  values.forEach((value) => {
    const tag = String(value ?? '').trim();
    const key = tag.toLowerCase().replace(/[-\s]+/g, '_');
    if (!key || key === 'internal' || seen.has(key)) return;
    seen.add(key);
    tags.push(tag);
  });

  return tags;
};

type RecipePreviewCardProps = {
  label: string;
  title: string;
  image?: string | null;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  tone?: 'before' | 'after';
};

const RecipePreviewCard: React.FC<RecipePreviewCardProps> = ({
  label,
  title,
  image,
  calories,
  protein,
  carbs,
  fat,
  fiber,
  tone = 'before',
}) => {
  const isAfter = tone === 'after';

  return (
    <div className={`rounded-xl border px-3 py-3 ${
      isAfter ? 'border-cyan-200 bg-cyan-50/50' : 'border-slate-200 bg-slate-50'
    }`}>
      <div className="flex gap-3">
        <img
          src={image || DEFAULT_RECIPE_IMAGE}
          alt={title}
          className="h-16 w-20 shrink-0 rounded-lg object-cover"
          onError={(event) => {
            const target = event.target as HTMLImageElement;
            target.onerror = null;
            target.src = DEFAULT_RECIPE_IMAGE;
          }}
        />
        <div className="min-w-0 flex-1">
          <p className={`text-xs font-semibold uppercase tracking-wide ${
            isAfter ? 'text-cyan-700' : 'text-slate-500'
          }`}>
            {label}
          </p>
          <p className="mt-1 line-clamp-2 text-sm font-semibold leading-snug text-slate-800">{title}</p>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600 sm:grid-cols-5">
        <span>
          <span className="block text-slate-400">Calories</span>
          <span className="font-semibold text-slate-800">{formatMacro(calories)} kcal</span>
        </span>
        <span>
          <span className="block text-slate-400">Protein</span>
          <span className="font-semibold text-slate-800">{formatMacro(protein)}g</span>
        </span>
        <span>
          <span className="block text-slate-400">Carbs</span>
          <span className="font-semibold text-slate-800">{formatMacro(carbs)}g</span>
        </span>
        <span>
          <span className="block text-slate-400">Fat</span>
          <span className="font-semibold text-slate-800">{formatMacro(fat)}g</span>
        </span>
        <span>
          <span className="block text-slate-400">Fiber</span>
          <span className="font-semibold text-slate-800">{formatMacro(fiber)}g</span>
        </span>
      </div>
    </div>
  );
};

const SmartEditModal: React.FC<SmartEditModalProps> = ({
  open,
  onOpenChange,
  meal,
  prompt,
  onPromptChange,
  onPresetSelect,
  onSendPrompt,
  suggestions,
  selectedSuggestionId,
  onSelectSuggestion,
  inProgressNotice,
  generating = false,
  applying = false,
  attemptsLeft,
  attemptsUsed,
  dailyLimit,
  generateDisabled = false,
}) => {
  if (!meal) {
    return null;
  }

  const hasInstruction = prompt.trim().length > 0;
  const hasGeneratedSuggestion = suggestions.length > 0;
  const selectedSuggestion =
    suggestions.find((candidate) => candidate.id === selectedSuggestionId) ?? null;
  const showBeforeAfter = Boolean(selectedSuggestion);
  const selectedSuggestionCuisines = visibleTags(selectedSuggestion?.cuisines).length > 0
    ? visibleTags(selectedSuggestion?.cuisines)
    : visibleTags(selectedSuggestion?.cuisineTypes);
  const attemptsBadgeLabel =
    typeof attemptsLeft === 'number' ? `${Math.max(0, attemptsLeft)} left` : null;
  const attemptsUsageLabel =
    typeof attemptsUsed === 'number' &&
    typeof dailyLimit === 'number' &&
    dailyLimit > 0
      ? `${Math.min(attemptsUsed, dailyLimit)}/${dailyLimit} used in 24h`
      : null;
  const shouldShowGenerateButton = !generateDisabled && !inProgressNotice;
  const generateButtonDisabled = !hasInstruction || generating || hasGeneratedSuggestion;
  const generateButtonTitle = hasGeneratedSuggestion
    ? 'Change the instruction to generate a new suggestion.'
    : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[88vh] max-h-[88vh] w-[94vw] max-w-[640px] flex-col gap-0 overflow-hidden rounded-[20px] border border-slate-200 p-0 shadow-xl [&>button]:hidden">
        <DialogTitle className="sr-only">Smart Edit</DialogTitle>
        <DialogDescription className="sr-only">
          Update the current meal by entering an instruction, then tap an AI-suggested replacement to apply it.
        </DialogDescription>
        <div className="shrink-0 border-b border-slate-200 px-5 py-4 pr-14">
          <button
            type="button"
            className="absolute right-6 top-5 text-slate-400 transition-colors hover:text-slate-600"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-cyan-500" />
            <h3 className="text-2xl font-semibold leading-none text-slate-800">Smart Edit</h3>
            {attemptsBadgeLabel && (
              <Tag variant="outlined" size="xs" className="bg-cyan-100 text-cyan-600">
                {attemptsBadgeLabel}
              </Tag>
            )}
          </div>
          <p className="mt-1.5 text-sm text-slate-500">AI will suggest a replacement based on your instruction — tap a suggestion to apply it</p>
          {attemptsUsageLabel && (
            <p className="mt-1 text-xs font-medium text-slate-500">{attemptsUsageLabel}</p>
          )}
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div className={showBeforeAfter ? 'grid gap-3 md:grid-cols-2' : ''}>
            <RecipePreviewCard
              label={showBeforeAfter ? 'Before Smart Edit' : 'Current Recipe'}
              title={meal.recipeName}
              image={meal.recipeImage}
              calories={meal.calories}
              protein={meal.protein}
              carbs={meal.carbs}
              fat={meal.fat}
              fiber={meal.fiber}
              tone="before"
            />
            {selectedSuggestion && (
              <RecipePreviewCard
                label="After Smart Edit"
                title={selectedSuggestion.title}
                image={selectedSuggestion.image}
                calories={selectedSuggestion.calories}
                protein={selectedSuggestion.protein}
                carbs={selectedSuggestion.carbs}
                fat={selectedSuggestion.fat}
                fiber={selectedSuggestion.fiber}
                tone="after"
              />
            )}
          </div>

          {selectedSuggestionCuisines.length > 0 && (
            <div className="flex flex-wrap gap-1 text-xs">
              {selectedSuggestionCuisines.slice(0, 3).map((cuisine, index) => (
                <span
                  key={`${cuisine}-${index}`}
                  className="rounded-sm bg-amber-50 px-1.5 py-0.5 font-medium text-amber-700"
                >
                  {getDietaryDisplayName(cuisine, 'cuisineTypes')}
                </span>
              ))}
            </div>
          )}

          <div>
            <p className="mb-2 text-lg font-medium text-slate-700">What would you like to change?</p>
            <Textarea
              value={prompt}
              onChange={(event) => onPromptChange(event.target.value)}
              rows={3}
              className="text-sm"
              placeholder='e.g., "Make it higher in fiber", "Swap for something vegetarian", "Reduce calories by 100"'
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {PRESET_PROMPTS.map((preset) => (
              <button
                key={preset}
                type="button"
                className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-200"
                onClick={() => onPresetSelect(preset)}
              >
                {preset}
              </button>
            ))}
          </div>

          {hasInstruction && (
            <div
              className="flex flex-col rounded-2xl border-2 border-cyan-500 bg-cyan-50/30 p-4"
            >
              <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-cyan-600">
                <Sparkles className="h-4 w-4" />
                AI Suggestion
              </div>

              {inProgressNotice && !generating && suggestions.length === 0 && (
                <div className="mt-3 rounded-xl border border-cyan-200 bg-cyan-100/60 px-3 py-2 text-xs text-cyan-700">
                  <span className="inline-flex items-center gap-2">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-500" />
                    {inProgressNotice}
                  </span>
                </div>
              )}

              {generating ? (
                <div className="py-8 text-center text-sm text-slate-500">Generating suggestion...</div>
              ) : suggestions.length > 0 ? (
                <div className="mt-3 space-y-3 pr-1">
                  {suggestions.map((suggestion) => {
                    const isSelected = suggestion.id === selectedSuggestionId;
                    const isApplying = applying && isSelected;
                    const generatedLabel = getGeneratedIndicatorLabel(suggestion);

                    return (
                      <button
                        key={suggestion.id}
                        type="button"
                        disabled={applying}
                        className={`w-full rounded-xl border p-3 text-left transition-colors disabled:cursor-not-allowed ${
                          isSelected
                            ? 'border-cyan-500 bg-cyan-50'
                            : 'border-slate-200 bg-white hover:border-cyan-300 disabled:opacity-60'
                        }`}
                        onClick={() => onSelectSuggestion?.(suggestion.id)}
                      >
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-3">
                          <img
                            src={suggestion.image || DEFAULT_RECIPE_IMAGE}
                            alt={suggestion.title}
                            className="h-20 w-24 rounded-xl object-cover"
                            onError={(event) => {
                              const target = event.target as HTMLImageElement;
                              target.onerror = null;
                              target.src = DEFAULT_RECIPE_IMAGE;
                            }}
                          />

                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                {generatedLabel ? (
                                  <Tag variant="outlined" size="xs" className="mb-1 bg-cyan-100 uppercase text-cyan-700">
                                    {generatedLabel}
                                  </Tag>
                                ) : null}
                                <p className="text-base font-medium text-cyan-700">{suggestion.title}</p>
                              </div>
                              {isSelected && (
                                <Tag variant="selected" size="xs" className="border-0 bg-cyan-500 text-white">
                                  {isApplying ? 'Applying...' : 'Selected'}
                                </Tag>
                              )}
                            </div>

                            <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-600">
                              <span>
                                <span className="block text-slate-400">Calories</span>
                                <span className="font-semibold text-slate-800">{formatMacro(suggestion.calories)} kcal</span>
                              </span>
                              <span>
                                <span className="block text-slate-400">Protein</span>
                                <span className="font-semibold text-slate-800">{formatMacro(suggestion.protein)}g</span>
                              </span>
                              <span>
                                <span className="block text-slate-400">Carbs</span>
                                <span className="font-semibold text-slate-800">{formatMacro(suggestion.carbs)}g</span>
                              </span>
                              <span>
                                <span className="block text-slate-400">Fat</span>
                                <span className="font-semibold text-slate-800">{formatMacro(suggestion.fat)}g</span>
                              </span>
                            </div>

                            <p className="mt-2 text-sm text-slate-500">
                              Best for: <span className="font-semibold text-slate-700">{suggestion.bestFor}</span>
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="py-8 text-center text-sm text-slate-500">
                  {inProgressNotice ? 'Waiting for AI to finish...' : 'No suggestion yet.'}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-slate-200 bg-white px-5 py-4">
          <div className="flex flex-wrap items-center justify-end gap-3">
              <Button
                type="button"
                variant="ghost"
                className="h-10 px-4 text-sm text-slate-600 hover:text-slate-800"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              {shouldShowGenerateButton ? (
                <Button
                  type="button"
                  className="h-10 rounded-xl bg-slate-800 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
                  disabled={generateButtonDisabled}
                  title={generateButtonTitle}
                  onClick={onSendPrompt}
                >
                  {generating ? 'Generating...' : 'Generate'}
                </Button>
              ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SmartEditModal;
